import React, { useEffect, useState } from 'react';
import {
  Camera, MapPin, Calendar, Stethoscope, BookOpen,
  CheckCircle2, Printer, Sparkles, Globe, Share2, ArrowRight, Lock, Mail, Loader2,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

/**
 * DoctorOnboardingGuide — printable 2-page A4 PDF-friendly handout for
 * Free / Gold-tier doctors.
 *
 * Why
 * ---
 * Doctors live with printed reference cards (drug formularies, BLS algos,
 * triage flowcharts). A 2-pager pinned next to the laptop is more useful
 * than any in-app tooltip. This page renders the same 5-step DIY funnel
 * as the in-app wizard, but laid out for a single Cmd+P / Ctrl+P → 2-page
 * PDF.
 *
 * Print rules
 * -----------
 *   - A4 portrait, 12 mm margins.
 *   - Page 1: hero + plan summary + steps 1-3 with "Keep ready" boxes.
 *   - Page 2: steps 4-5 + go-live + FAQ + footer.
 *   - All chrome (header, "Print" button) hidden via `print:hidden`.
 *   - Forced page break between sections via `.page-break`.
 */
const STEPS = [
  {
    n: 1, label: 'Tell patients about you', time: 3, icon: Camera,
    keep_ready: [
      'A sharp portrait photo (selfie / studio — square crop works best)',
      'NMC or State Medical Council registration number',
      '1-line tagline (e.g. "Cardiologist with 12 years\' bypass experience")',
      '2-3 line bio in your own voice',
      'Specialisation(s) — pick from the list',
    ],
    output: 'Patients see a credible "About me" panel with your photo, qualifications and tagline.',
  },
  {
    n: 2, label: 'Add your clinic location', time: 1, icon: MapPin,
    keep_ready: [
      'Your clinic / hospital name as it shows on Google Maps',
      'Or just the address — pincode + city is enough to start',
    ],
    output: 'Patients see verified clinic name, address, exact map pin and driving time from their pincode.',
  },
  {
    n: 3, label: 'Set your OPD hours', time: 2, icon: Calendar,
    keep_ready: [
      'Days of the week you see patients',
      'Morning + evening time slots per clinic',
      'Approximate slot duration (e.g. 15 minutes per patient)',
    ],
    output: 'Booking widget on your public profile shows real available slots — patients book in one tap.',
  },
  {
    n: 4, label: 'Pick your top procedures + prices', time: 2, icon: Stethoscope,
    keep_ready: [
      'Your top 5 procedures or consultations',
      'Rough price range for each (e.g. ₹500 – ₹800 OPD consult)',
    ],
    helper: 'Tap "Auto-fill from catalog" — 30+ ready procedures load in one click. Edit prices only on the ones you offer.',
    output: 'Procedure cards on your profile rank for "[procedure] cost in [city]" searches on Google.',
  },
  {
    n: 5, label: 'Make it sound like you · Hand-written on Free', time: 5, icon: BookOpen,
    keep_ready: [
      '5 minutes to write your bio, procedures and about in your own words',
      'Personal anecdotes / your way of explaining things in 1-2 lines',
    ],
    helper: 'Free-tier doctors hand-write every section (no AI templates) — this is what makes DocSite\'s content quality stand out and what Google rewards. Coffee Premium adds a one-time AI on-ramp; TurboCharge adds monthly AI drafts. Either way, ≥ 40% authentic voice is what ranks.',
    output: 'Your profile reads like YOU — not generic copy. Google trusts it; patients trust it.',
  },
];

const WHATS_FREE = [
  'Personal website at docsite.care/dr/your-name (SEO-indexed on Google)',
  'Up to 2 verified clinic locations with precise map pins',
  'Weekly OPD schedule + live booking widget',
  '30+ auto-seeded procedures with prices (edit any time)',
  'WhatsApp + QR code share for your patients',
  'Leads inbox (every patient enquiry comes here)',
  'Patient feedback & ratings under each procedure',
  'Basic visibility analytics (profile views, leads, bookings)',
];

const WHATS_PREMIUM = [
  'One-time AI on-ramp · 3 lifetime pulls (bio + procedures + FAQ)',
  'In-app voice calls + chat with patients',
  'Custom branding (fonts, colours, badges)',
  'For monthly AI articles + AI drafts, upgrade to TurboCharge',
];

function EmailMeButton() {
  const [state, setState] = useState('idle');
  const [msg, setMsg] = useState('');
  const send = async () => {
    setState('sending'); setMsg('');
    try {
      const r = await api.post('/private/email-onboarding-guide');
      setState('sent'); setMsg(`Sent to ${r.data.to}.`);
    } catch (e) {
      setState('error'); setMsg(e.response?.data?.detail || 'Failed to send.');
    }
  };
  return (
    <div className="flex flex-col items-end gap-1">
      <button type="button" onClick={send} disabled={state === 'sending' || state === 'sent'}
        className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl text-xs font-semibold hover:bg-slate-50 disabled:opacity-60"
        data-testid="email-me-guide-button">
        {state === 'sending' ? <><Loader2 size={12} className="animate-spin" /> Sending…</> :
         state === 'sent' ? <>✓ Sent to inbox</> :
         <><Mail size={12} /> Email me this guide</>}
      </button>
      {msg && <p className={`text-[10px] ${state === 'error' ? 'text-red-600' : 'text-slate-500'}`}>{msg}</p>}
    </div>
  );
}

export default function DoctorOnboardingGuide() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) navigate('/login?next=/onboarding-guide', { replace: true });
    else if (user.role !== 'doctor') navigate('/', { replace: true });
  }, [user, loading, navigate]);

  if (loading || !user || user.role !== 'doctor') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-slate-500 gap-3" data-testid="guide-auth-gate">
        <Lock size={28} />
        <p className="text-sm">Doctor sign-in required to view the onboarding guide.</p>
        <Link to="/login?next=/onboarding-guide" className="text-sky-600 text-sm font-medium hover:underline">
          Sign in as a doctor →
        </Link>
      </div>
    );
  }

  return _RenderGuide({ emailButton: <EmailMeButton /> });
}

function _RenderGuide({ emailButton }) {
  return (
    <div className="bg-slate-100 min-h-screen py-8 print:bg-white print:py-0">
      {/* In-screen chrome — hidden on print */}
      <div className="max-w-3xl mx-auto mb-4 px-4 flex items-center justify-between print:hidden">
        <h1 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
          DocSite · Doctor onboarding 2-pager
        </h1>
        <div className="flex items-center gap-2">
          {emailButton}
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-sky-600 text-white rounded-xl text-sm font-semibold hover:bg-sky-700 shadow-sm"
            data-testid="print-button"
          >
            <Printer size={14} /> Print / Save as PDF
          </button>
        </div>
      </div>

      {/* ────────── PAGE 1 ────────── */}
      <section className="bg-white max-w-3xl mx-auto rounded-2xl shadow-md print:shadow-none print:rounded-none print:max-w-none p-10 print:p-8" style={{ fontFamily: 'Outfit, sans-serif' }}>

        <div className="flex items-baseline justify-between border-b border-slate-200 pb-4 mb-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-sky-600 font-bold">Free · Gold Plan</p>
            <h1 className="text-3xl font-bold text-slate-900 mt-1">Your DocSite profile in 11 minutes</h1>
            <p className="text-sm text-slate-600 mt-2">
              Print this and pin it next to your laptop. Five steps. Each step lists exactly what to keep in front of you before you tap the button.
            </p>
          </div>
          <div className="text-right shrink-0 hidden md:block">
            <p className="text-[11px] uppercase tracking-widest text-slate-400">Plan</p>
            <p className="text-2xl font-bold text-emerald-600">Gold</p>
            <p className="text-[11px] text-slate-500">FREE forever</p>
          </div>
        </div>

        {/* What you get */}
        <div className="grid md:grid-cols-2 gap-5 mb-7">
          <div className="rounded-xl bg-emerald-50/50 border border-emerald-200 p-4">
            <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-2">Included on Gold (Free)</p>
            <ul className="space-y-1.5">
              {WHATS_FREE.map((f, i) => (
                <li key={i} className="text-xs text-slate-700 flex items-start gap-2">
                  <CheckCircle2 size={12} className="text-emerald-500 mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl bg-amber-50/40 border border-amber-200 p-4">
            <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-2">Premium adds (when you're ready)</p>
            <ul className="space-y-1.5">
              {WHATS_PREMIUM.map((f, i) => (
                <li key={i} className="text-xs text-slate-700 flex items-start gap-2">
                  <Sparkles size={12} className="text-amber-500 mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-slate-500 mt-2 italic">Skip these for now — get to "live on Google" first.</p>
          </div>
        </div>

        {/* Steps 1-3 */}
        <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">The 5-step DIY funnel</p>

        {STEPS.slice(0, 3).map((s) => {
          const Icon = s.icon;
          return (
            <article key={s.n} className="border-l-4 border-sky-400 pl-5 mb-6">
              <div className="flex items-center gap-2.5 mb-1.5">
                <div className="w-7 h-7 rounded-full bg-sky-600 text-white flex items-center justify-center text-sm font-bold">{s.n}</div>
                <Icon size={16} className="text-sky-600" />
                <h3 className="text-base font-bold text-slate-900">{s.label}</h3>
                <span className="ml-auto text-xs font-semibold text-slate-500">~ {s.time} min</span>
              </div>
              <div className="grid md:grid-cols-[1fr,1fr] gap-3">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Keep ready</p>
                  <ul className="space-y-1">
                    {s.keep_ready.map((k, i) => (
                      <li key={i} className="text-xs text-slate-700 flex items-start gap-1.5">
                        <span className="text-sky-400 mt-1">▸</span><span>{k}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 mb-1">Result</p>
                  <p className="text-xs text-slate-700">{s.output}</p>
                  {s.helper && (
                    <p className="text-[11px] text-sky-700 bg-sky-50 border border-sky-100 rounded-md px-2 py-1.5 mt-2 italic">{s.helper}</p>
                  )}
                </div>
              </div>
            </article>
          );
        })}

        <p className="text-[11px] text-slate-400 text-center mt-6 italic">— continued on page 2 —</p>
      </section>

      {/* Force page break before page 2 */}
      <div className="page-break" />

      {/* ────────── PAGE 2 ────────── */}
      <section className="bg-white max-w-3xl mx-auto rounded-2xl shadow-md print:shadow-none print:rounded-none print:max-w-none p-10 print:p-8 mt-8 print:mt-0" style={{ fontFamily: 'Outfit, sans-serif' }}>
        <div className="flex items-baseline justify-between border-b border-slate-200 pb-4 mb-6">
          <h2 className="text-2xl font-bold text-slate-900">Page 2 · The final 5 minutes + going live</h2>
          <p className="text-[11px] text-slate-400">DocSite Gold plan · printable 2-pager</p>
        </div>

        {/* Steps 4-5 */}
        {STEPS.slice(3).map((s) => {
          const Icon = s.icon;
          return (
            <article key={s.n} className="border-l-4 border-sky-400 pl-5 mb-6">
              <div className="flex items-center gap-2.5 mb-1.5">
                <div className="w-7 h-7 rounded-full bg-sky-600 text-white flex items-center justify-center text-sm font-bold">{s.n}</div>
                <Icon size={16} className="text-sky-600" />
                <h3 className="text-base font-bold text-slate-900">{s.label}</h3>
                <span className="ml-auto text-xs font-semibold text-slate-500">~ {s.time} min</span>
              </div>
              <div className="grid md:grid-cols-[1fr,1fr] gap-3">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Keep ready</p>
                  <ul className="space-y-1">
                    {s.keep_ready.map((k, i) => (
                      <li key={i} className="text-xs text-slate-700 flex items-start gap-1.5">
                        <span className="text-sky-400 mt-1">▸</span><span>{k}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 mb-1">Result</p>
                  <p className="text-xs text-slate-700">{s.output}</p>
                  {s.helper && (
                    <p className="text-[11px] text-sky-700 bg-sky-50 border border-sky-100 rounded-md px-2 py-1.5 mt-2 italic">{s.helper}</p>
                  )}
                </div>
              </div>
            </article>
          );
        })}

        {/* Going live */}
        <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-sky-50 border-2 border-emerald-300 p-5 mt-2 mb-6">
          <div className="flex items-center gap-2.5 mb-2">
            <Globe size={18} className="text-emerald-600" />
            <h3 className="text-base font-bold text-emerald-900">You're live!</h3>
          </div>
          <p className="text-xs text-slate-700">
            Once Step 5 turns green, your public URL <strong>docsite.care/dr/your-slug</strong> is indexable by Google. The dashboard celebration card will give you a <strong>one-tap WhatsApp share</strong> — paste it in 3 patient groups + your alumni group on day one. That's the single highest-leverage thing you can do for Google ranking.
          </p>
          <div className="flex flex-wrap gap-3 mt-3 text-[11px] text-slate-600">
            <span className="inline-flex items-center gap-1 bg-white border border-slate-200 px-2 py-1 rounded-full">
              <Share2 size={11} className="text-sky-500" /> WhatsApp shares = SEO ranking signal
            </span>
            <span className="inline-flex items-center gap-1 bg-white border border-slate-200 px-2 py-1 rounded-full">
              <ArrowRight size={11} className="text-sky-500" /> Add to Google Business Profile bio
            </span>
            <span className="inline-flex items-center gap-1 bg-white border border-slate-200 px-2 py-1 rounded-full">
              <ArrowRight size={11} className="text-sky-500" /> Paste in your hospital email signature
            </span>
          </div>
        </div>

        {/* FAQ */}
        <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Quick FAQ</p>
        <dl className="space-y-2.5 text-xs">
          <div>
            <dt className="font-bold text-slate-900">Can I skip the wizard and explore?</dt>
            <dd className="text-slate-600 mt-0.5">Yes — tap "Skip for now" top-right. The full dashboard opens. The wizard returns on your next login until you publish.</dd>
          </div>
          <div>
            <dt className="font-bold text-slate-900">Is anything I add visible publicly before Step 5?</dt>
            <dd className="text-slate-600 mt-0.5">No. Your profile stays in <em>draft</em> until all 5 steps are green. Patients and Google see nothing until you're ready.</dd>
          </div>
          <div>
            <dt className="font-bold text-slate-900">Do I need GST / hospital registration to start?</dt>
            <dd className="text-slate-600 mt-0.5">No. Only your individual NMC / state council registration number — same one you put on prescriptions.</dd>
          </div>
          <div>
            <dt className="font-bold text-slate-900">How long until Google indexes my page?</dt>
            <dd className="text-slate-600 mt-0.5">Typically 2–7 days for first crawl, 4–6 months for steady daily leads as backlinks + WhatsApp shares accrue. SLA: at least one daily-or-near-daily quality lead by month 6.</dd>
          </div>
          <div>
            <dt className="font-bold text-slate-900">What if my clinic isn't on the address picker?</dt>
            <dd className="text-slate-600 mt-0.5">Tap "Skip and enter manually" — the form accepts any address. Pincode auto-validates city/state. Founder team verifies the pin manually within 24 hrs.</dd>
          </div>
        </dl>

        <footer className="mt-7 pt-4 border-t border-slate-200 text-[11px] text-slate-500 flex items-center justify-between">
          <span>docsite.care · The doctor-first personal-website platform</span>
          <span>Version 2.0 · Print-friendly · Last updated Feb 2026</span>
        </footer>
      </section>

      {/* Print rules */}
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          body { background: white; }
          .page-break { page-break-after: always; }
        }
      `}</style>
    </div>
  );
}
